import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE } from "../../config/apiConfig";
import { FaCheckCircle } from "react-icons/fa";
import LoadingOverlay from "../../components/loadingOverlay";
import { useToast } from "../../hooks/useToast";

export default function VerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email; // Passed from register/login

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { showToast, ToastContainer } = useToast();

  useEffect(() => {
    if (!email) navigate("/login"); // Safety redirect if no email
  }, [email, navigate]);

  // Countdown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (code.length !== 6) {
      setError("Please enter a 6-digit code.");
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/api/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(data?.message || "Verification failed.");

      let countdown = 5; // seconds before redirect
      // first toast
      showToast(
        `Verification successful!\nYour account is now active.\nRedirecting to Login page in ${countdown}s...`,
        "success"
      );

      // update every second
      const interval = setInterval(() => {
        countdown--;
        showToast(
          `Verification successful!\nYour account is now active.\nRedirecting to Login page in ${countdown}s...`,
          "success",
          "top",
          true,
          5500
        );
        if (countdown <= 0) clearInterval(interval);
      }, 1000);

      // redirect after timeout
      setTimeout(() => {
        clearInterval(interval);
        navigate("/login");
      }, 5000);

    } catch (err) {
      showToast(err.message || "Verification failed.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (cooldown > 0) return;

    setResending(true);

    try {
      const res = await fetch(`${API_BASE}/api/resend-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(data?.message || "Failed to resend code.");
      showToast("New verification code sent!", "success");
      setCooldown(60); // Start countdown
    } catch (err) {
      showToast(err.message || "Failed to resend code.", "error");
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="w-full flex-grow flex flex-col items-center justify-center py-16">
      <div className="bg-white rounded-xl shadow p-8 w-full max-w-md">
        <h1 className="text-2xl font-semibold text-center mb-6 text-gray-800">
          <FaCheckCircle className="inline-block text-green-600 mr-2" />
          Email Verification
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Enter 6-digit Verification Code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              className="w-full border rounded-lg px-3 py-2 text-center text-lg tracking-widest font-semibold placeholder:italic placeholder:text-gray-400"
              maxLength={6}
              placeholder="123456"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={`w-full px-5 py-2 rounded-lg text-white ${
              submitting ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {submitting ? "Verifying..." : "Verify"}
          </button>
        </form>

        <div className="text-center mt-4">
          <button
            onClick={handleResend}
            disabled={cooldown > 0 || resending}
            className={`text-sm ${
              cooldown > 0 || resending
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-600 hover:underline"
            }`}
          >
            {cooldown > 0 || resending
              ? `Resend code in ${cooldown}s`
              : "Didn’t receive code? Resend"}
          </button>
        </div>
      </div>
      <LoadingOverlay show={submitting || resending} text="Verifying..." />
      <ToastContainer />
    </div>
  );
}
